import { useState, useEffect, useCallback } from 'react';

export interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  view: string;
  badge?: number;
  visible?: boolean;
}

interface UseSidebarOrderOptions {
  storageKey: string;
  defaultItems: NavItem[];
}

export const useSidebarOrder = ({ storageKey, defaultItems }: UseSidebarOrderOptions) => {
  const [items, setItems] = useState<NavItem[]>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const savedOrder: string[] = JSON.parse(stored);
        // Reorder defaultItems based on saved order, keeping any new items at the end
        const orderedItems: NavItem[] = [];
        const remainingItems = [...defaultItems];
        
        savedOrder.forEach(id => {
          const itemIndex = remainingItems.findIndex(item => item.id === id);
          if (itemIndex !== -1) {
            orderedItems.push(remainingItems[itemIndex]);
            remainingItems.splice(itemIndex, 1);
          }
        });
        
        // Add any remaining items (new items not in saved order)
        return [...orderedItems, ...remainingItems];
      }
    } catch (e) {
      console.error('Error loading sidebar order:', e);
    }
    return defaultItems;
  });

  // Update items when defaultItems change (e.g., feature toggles)
  useEffect(() => {
    setItems(prevItems => {
      const currentOrder = prevItems.map(item => item.id);
      const orderedItems: NavItem[] = [];
      const remainingDefaults = [...defaultItems];

      currentOrder.forEach(id => {
        const itemIndex = remainingDefaults.findIndex(item => item.id === id);
        if (itemIndex !== -1) {
          orderedItems.push(remainingDefaults[itemIndex]);
          remainingDefaults.splice(itemIndex, 1);
        }
      });

      return [...orderedItems, ...remainingDefaults];
    });
  }, [defaultItems]);

  const reorderItems = useCallback((activeId: string, overId: string) => {
    setItems(prevItems => {
      const oldIndex = prevItems.findIndex(item => item.id === activeId);
      const newIndex = prevItems.findIndex(item => item.id === overId);

      if (oldIndex === -1 || newIndex === -1) return prevItems;

      const newItems = [...prevItems];
      const [movedItem] = newItems.splice(oldIndex, 1);
      newItems.splice(newIndex, 0, movedItem);

      // Save to localStorage
      localStorage.setItem(storageKey, JSON.stringify(newItems.map(item => item.id)));

      return newItems;
    });
  }, [storageKey]);

  const resetOrder = useCallback(() => {
    localStorage.removeItem(storageKey);
    setItems(defaultItems);
  }, [storageKey, defaultItems]);

  return {
    items: items.filter(item => item.visible !== false),
    reorderItems,
    resetOrder,
  };
};
